/**
 * AgentTokenModal — F18 T18.3.
 *
 * Pokazuje plain token agenta JEDEN RAZ po wygenerowaniu (response z POST
 * /regenerate-agent-token). Klient kopiuje do configu agenta — po zamknięciu
 * modal-a plain token NIGDY już nie będzie widoczny (musimy regenerować).
 *
 * Warning UX: czerwony pasek „Skopiuj teraz — nie pokażemy ponownie" + button
 * „Skopiuj" z visual feedback (status „Skopiowano!" po klik).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, KeyRound } from 'lucide-react';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';

interface Props {
  open: boolean;
  /** Plain token zwrócony z POST /regenerate-agent-token */
  token: string;
  integrationName: string;
  onClose: () => void;
}

export function AgentTokenModal({ open, token, integrationName, onClose }: Props) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select w input — user kopiuje ręcznie Ctrl+C
      const input = document.getElementById('agent-token-input') as HTMLInputElement | null;
      input?.select();
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      role="alertdialog"
      title={
        <span className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-accent-500" aria-hidden="true" />
          {t('tenant.settings.integrations.agentToken.title')}
        </span>
      }
      size="lg"
      testId="agent-token-modal"
      // Jednorazowy sekret: przypadkowy klik w tło ani Esc nie mogą go schować, zanim ktoś go
      // skopiuje. Zamknąć można tylko X lub „Rozumiem".
      closeOnBackdrop={false}
      closeOnEsc={false}
      footer={
        <Button variant="primary" onClick={onClose} data-testid="agent-token-modal-close-btn">
          {t('tenant.settings.integrations.agentToken.understood')}
        </Button>
      }
    >
      <div className="space-y-4">
        <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3">
          <p className="text-sm font-medium text-red-600 dark:text-red-400">
            {t('tenant.settings.integrations.agentToken.warning')}
          </p>
          <p className="mt-1 text-xs text-text-secondary">
            {t('tenant.settings.integrations.agentToken.warningDetail', {
              name: integrationName,
            })}
          </p>
        </div>

        <div>
          <label
            htmlFor="agent-token-input"
            className="mb-1 block text-sm font-medium text-text-secondary"
          >
            {t('tenant.settings.integrations.agentToken.tokenLabel')}
          </label>
          <div className="flex gap-2">
            <input
              id="agent-token-input"
              type="text"
              value={token}
              readOnly
              onClick={(e) => (e.currentTarget as HTMLInputElement).select()}
              className="flex-1 rounded-md border border-border-primary bg-bg-secondary px-3 py-2 font-mono text-xs text-text-primary"
              data-testid="agent-token-modal-token-input"
            />
            <Button
              variant={copied ? 'ghost' : 'primary'}
              onClick={handleCopy}
              // Fokus startowy na „Skopiuj", nie na X: Enter/Spacja tuż po otwarciu kopiuje
              // token zamiast zamykać okno (token widać tylko raz).
              autoFocus
              leftIcon={
                copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />
              }
              data-testid="agent-token-modal-copy-btn"
            >
              {copied
                ? t('tenant.settings.integrations.agentToken.copied')
                : t('tenant.settings.integrations.agentToken.copy')}
            </Button>
          </div>
        </div>

        <div className="rounded-md bg-bg-secondary p-3">
          <p className="text-xs font-medium text-text-secondary">
            {t('tenant.settings.integrations.agentToken.howToUse')}
          </p>
          <ol className="mt-2 list-inside list-decimal space-y-1 text-xs text-text-tertiary">
            <li>{t('tenant.settings.integrations.agentToken.step1')}</li>
            <li>{t('tenant.settings.integrations.agentToken.step2')}</li>
            <li>{t('tenant.settings.integrations.agentToken.step3')}</li>
          </ol>
        </div>
      </div>
    </Modal>
  );
}
